import React from 'react';
import { Card, Typography, Tooltip } from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { StatusTag } from '../../../../components/ui/status-tag';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { C } from '../../constants/design-tokens';

const { Text } = Typography;

interface ProjectCardProps {
  projectId: string;
  name: string;
  status: string;
  totalScenes: number;
  completedScenes: number;
  failedScenes: number;
  createdAt: string;
  updatedAt: string | null;
  hasPayload: boolean;
  onEdit: (projectId: string) => void;
  onRegenerate: (projectId: string) => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  processing: { label: 'Procesando', color: C.warning, bgColor: C.warningSurface },
  completed: { label: 'Completado', color: C.success, bgColor: C.successSurface },
  partial: { label: 'Parcial', color: C.warning, bgColor: C.warningSurface },
  failed: { label: 'Error', color: C.danger, bgColor: C.dangerSurface }
};

const INITIAL_COLORS: Record<string, { bg: string; text: string }> = {
  processing: { bg: C.warningSurface, text: C.warning },
  completed: { bg: C.successSurface, text: C.success },
  partial: { bg: C.warningSurface, text: C.warning },
  failed: { bg: C.dangerSurface, text: C.danger }
};

const ProjectInitial: React.FC<{ name: string; status: string }> = ({ name, status }) => {
  const colors = INITIAL_COLORS[status] || { bg: C.surface, text: C.textMuted };
  const initial = (name || '?').charAt(0).toUpperCase();

  return (
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 8,
        backgroundColor: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 22,
        fontWeight: 700,
        color: colors.text
      }}
    >
      {initial}
    </div>
  );
};

export const ProjectCard: React.FC<ProjectCardProps> = ({
  projectId,
  name,
  status,
  totalScenes,
  completedScenes,
  createdAt,
  updatedAt,
  hasPayload,
  onEdit,
  onRegenerate
}) => {
  const { fontSize } = useResponsiveSizes();
  const [isHovered, setIsHovered] = React.useState(false);
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.processing;

  const displayDate = updatedAt ?? createdAt;
  const formattedDate = new Date(displayDate).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const scenesLabel =
    status === 'completed'
      ? `${totalScenes} ${totalScenes === 1 ? 'escena' : 'escenas'}`
      : `${completedScenes}/${totalScenes} escenas`;

  return (
    <div style={{ width: '100%' }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <Card
        style={{
          width: '100%',
          borderRadius: 8,
          transition: 'all 0.2s ease',
          transform: isHovered ? 'translateY(-2px)' : 'none',
          boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.08)' : 'none'
        }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ProjectInitial name={name} status={status} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: fontSize.base,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {name || 'Sin título'}
            </Text>
            <Text style={{ fontSize: fontSize.sm, color: C.textSecondary, display: 'block' }}>
              {scenesLabel} · {formattedDate}
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <StatusTag label={config.label} size='small' />
            {(status === 'completed' || status === 'partial') && (
              <Tooltip title='Editar proyecto'>
                <EditOutlined
                  onClick={() => onEdit(projectId)}
                  style={{ fontSize: 18, color: C.accent, cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            {hasPayload && (
              <Tooltip title='Regenerar proyecto'>
                <ReloadOutlined
                  onClick={() => onRegenerate(projectId)}
                  style={{ fontSize: 18, color: C.info, cursor: 'pointer' }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
